import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemDecorator,
  ListItemContent,
  Divider,
  LinearProgress,
  Stack,
  Sheet,
} from '@mui/joy';
import {
  CheckCircle as CheckIcon,
  XCircle as ErrorIcon,
  Info as InfoIcon,
  Play as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  ArrowsClockwise as RefreshIcon,
  DownloadSimple as DownloadIcon,
  Eye as ViewIcon,
  ArrowLeft as BackIcon,
  Gauge as SpeedIcon,
} from '@phosphor-icons/react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../../lib/api';

interface DeploymentStep {
  id: string;
  name: string;
  description: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';
  started_at?: string;
  completed_at?: string;
  duration?: number;
  progress_percentage?: number;
  details?: string;
  error_message?: string;
}

interface DeploymentMetrics {
  files_uploaded: number;
  total_files: number;
  bytes_uploaded: number;
  total_bytes: number;
  upload_speed: number;
  estimated_completion: string;
  current_file?: string;
}

interface DeploymentProgressData {
  deployment: {
    id: string;
    status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
    environment: 'staging' | 'production';
    created_at: string;
    started_at?: string;
    completed_at?: string;
    deployed_by: string;
    version: string;
  };
  steps: DeploymentStep[];
  metrics: DeploymentMetrics;
  logs: Array<{
    timestamp: string;
    level: 'info' | 'warning' | 'error' | 'debug';
    message: string;
    component?: string;
  }>;
  can_cancel: boolean;
  deployment_url?: string;
}

export const DeploymentProgress: React.FC = () => {
  const { siteId, deploymentId } = useParams<{ siteId: string; deploymentId: string }>();
  const navigate = useNavigate();
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Fetch deployment progress
  const { data: progressData, isLoading } = useQuery<DeploymentProgressData>({
    queryKey: ['deployment-progress', deploymentId],
    queryFn: async (): Promise<DeploymentProgressData> => {
      const response = await api.get(`/api/cms/deployments/${deploymentId}/status/`);
      return response.data as DeploymentProgressData;
    },
    refetchInterval: autoRefresh ? 2000 : false, // Refresh every 2 seconds when active
    enabled: !!deploymentId,
  });

  // Cancel deployment mutation
  const cancelMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post(`/api/cms/deployments/${deploymentId}/cancel/`, {});
      return response.data;
    },
    onSuccess: () => {
      setAutoRefresh(false);
    },
  });

  // Stop auto-refresh when deployment completes
  useEffect(() => {
    if (progressData?.deployment.status === 'completed' || 
        progressData?.deployment.status === 'failed' || 
        progressData?.deployment.status === 'cancelled') {
      setAutoRefresh(false);
    }
  }, [progressData?.deployment.status]);

  const handleCancel = async () => {
    if (window.confirm('Are you sure you want to cancel this deployment?')) {
      await cancelMutation.mutateAsync();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'failed': return 'danger';
      case 'in_progress': return 'warning';
      case 'cancelled': return 'neutral';
      default: return 'neutral';
    }
  };

  const getStepIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckIcon color="var(--joy-palette-success-500)" />;
      case 'failed': return <ErrorIcon color="var(--joy-palette-danger-500)" />;
      case 'in_progress': return <PlayIcon color="var(--joy-palette-warning-500)" />;
      case 'skipped': return <InfoIcon />;
      default: return <PauseIcon />;
    }
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '—';
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
  };

  const formatSpeed = (bytesPerSecond: number) => {
    return `${formatBytes(bytesPerSecond)}/s`;
  };

  const getOverallProgress = () => {
    if (!progressData) return 0;
    const completedSteps = progressData.steps.filter(step => step.status === 'completed').length;
    const totalSteps = progressData.steps.length;
    return totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;
  };

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography level="h2">Deployment Progress</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  if (!progressData) {
    return (
      <Box p={3}>
        <Alert color="danger">Failed to load deployment progress</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant="outlined"
            startDecorator={<BackIcon />}
            onClick={() => navigate(`/cms/sites/${siteId}/deploy`)}
          >
            Back to Deploy
          </Button>
          <Box>
            <Typography level="h2">Deployment Progress</Typography>
            <Typography level="body-md" color="neutral">
              {progressData.deployment.environment} deployment • v{progressData.deployment.version}
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startDecorator={<RefreshIcon />}
            onClick={() => setAutoRefresh(!autoRefresh)}
            color={autoRefresh ? 'success' : 'neutral'}
          >
            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
          </Button>
          {progressData.can_cancel && (
            <Button
              variant="outlined"
              color="danger"
              startDecorator={<StopIcon />}
              onClick={handleCancel}
              loading={cancelMutation.isPending}
            >
              Cancel
            </Button>
          )}
        </Stack>
      </Box>

      <Grid container spacing={3}>
        {/* Main Progress */}
        <Grid xs={12} lg={8}>
          <Stack spacing={3}>
            {/* Overall Status */}
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography level="h4">Deployment Status</Typography>
                  <Chip 
                    size="lg" 
                    color={getStatusColor(progressData.deployment.status)}
                  >
                    {progressData.deployment.status.replace('_', ' ').toUpperCase()}
                  </Chip>
                </Box>
                
                <LinearProgress 
                  value={getOverallProgress()}
                  sx={{ mb: 2 }}
                  size="lg"
                />
                
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography level="body-sm" color="neutral">
                    {Math.round(getOverallProgress())}% complete
                  </Typography>
                  <Typography level="body-sm" color="neutral">
                    Started {new Date(progressData.deployment.created_at).toLocaleTimeString()}
                  </Typography>
                </Box>

                {progressData.deployment.status === 'completed' && progressData.deployment_url && (
                  <Alert color="success" sx={{ mt: 2 }}>
                    <Typography level="title-sm">Deployment Complete!</Typography>
                    <Typography level="body-sm">
                      Your site has been successfully deployed.
                    </Typography>
                    <Button
                      size="sm"
                      variant="solid"
                      startDecorator={<ViewIcon />}
                      onClick={() => window.open(progressData.deployment_url, '_blank')}
                      sx={{ mt: 1 }}
                    >
                      View Live Site
                    </Button>
                  </Alert>
                )}

                {progressData.deployment.status === 'failed' && (
                  <Alert color="danger" sx={{ mt: 2 }}>
                    <Typography level="title-sm">Deployment Failed</Typography>
                    <Typography level="body-sm">
                      The deployment encountered an error. Check the logs below for details.
                    </Typography>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Deployment Steps */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" mb={2}>Deployment Steps</Typography>
                <List>
                  {progressData.steps.map((step, index) => (
                    <React.Fragment key={step.id}>
                      <ListItem>
                        <ListItemDecorator>
                          {getStepIcon(step.status)}
                        </ListItemDecorator>
                        <ListItemContent>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box>
                              <Typography level="title-sm">{step.name}</Typography>
                              <Typography level="body-sm" color="neutral">
                                {step.description}
                              </Typography>
                              {step.status === 'in_progress' && step.progress_percentage !== undefined && (
                                <LinearProgress 
                                  value={step.progress_percentage}
                                  sx={{ mt: 1, width: 200 }}
                                  size="sm"
                                />
                              )}
                              {step.error_message && (
                                <Typography level="body-sm" color="danger">
                                  {step.error_message}
                                </Typography>
                              )}
                            </Box>
                            <Box textAlign="right">
                              <Typography level="body-xs" color="neutral">
                                {formatDuration(step.duration)}
                              </Typography>
                              {step.status === 'completed' && step.completed_at && (
                                <Typography level="body-xs" color="neutral">
                                  {new Date(step.completed_at).toLocaleTimeString()}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        </ListItemContent>
                      </ListItem>
                      {index < progressData.steps.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Logs */}
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography level="h4">Deployment Logs</Typography>
                  <Button
                    size="sm"
                    variant="outlined"
                    color="primary"
                    startDecorator={<DownloadIcon />}
                  >
                    Download Logs
                  </Button>
                </Box>
                
                <Sheet
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 'sm',
                    backgroundColor: 'background.surface',
                    maxHeight: 300,
                    overflow: 'auto',
                  }}
                >
                  <Typography component="pre" level="body-xs" sx={{ fontFamily: 'monospace' }}>
                    {progressData.logs.map((log, index) => (
                      <Box key={index} sx={{ mb: 0.5 }}>
                        <Typography 
                          component="span" 
                          level="body-xs" 
                          color="neutral"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          [{new Date(log.timestamp).toLocaleTimeString()}]
                        </Typography>
                        {' '}
                        <Typography 
                          component="span" 
                          level="body-xs"
                          color={
                            log.level === 'error' ? 'danger' :
                            log.level === 'warning' ? 'warning' :
                            log.level === 'info' ? 'primary' : 'neutral'
                          }
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {log.level.toUpperCase()}
                        </Typography>
                        {' '}
                        <Typography 
                          component="span" 
                          level="body-xs"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {log.message}
                        </Typography>
                      </Box>
                    ))}
                  </Typography>
                </Sheet>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Metrics Sidebar */}
        <Grid xs={12} lg={4}>
          <Stack spacing={3}>
            {/* Upload Progress */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" mb={2}>Upload Progress</Typography>
                <Stack spacing={2}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography level="body-sm">Files</Typography>
                      <Typography level="body-sm">
                        {progressData.metrics.files_uploaded}/{progressData.metrics.total_files}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      value={(progressData.metrics.files_uploaded / progressData.metrics.total_files) * 100}
                      size="sm"
                    />
                  </Box>
                  
                  <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography level="body-sm">Data</Typography>
                      <Typography level="body-sm">
                        {formatBytes(progressData.metrics.bytes_uploaded)}/{formatBytes(progressData.metrics.total_bytes)}
                      </Typography>
                    </Box>
                    <LinearProgress 
                      value={(progressData.metrics.bytes_uploaded / progressData.metrics.total_bytes) * 100}
                      size="sm"
                    />
                  </Box>

                  {progressData.metrics.current_file && (
                    <Box>
                      <Typography level="body-sm" color="neutral">
                        Current: {progressData.metrics.current_file}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Performance Metrics */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" mb={2}>Performance</Typography>
                <List size="sm">
                  <ListItem>
                    <ListItemDecorator><SpeedIcon /></ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">Upload Speed</Typography>
                      <Typography level="body-xs" color="neutral">
                        {formatSpeed(progressData.metrics.upload_speed)}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                  <ListItem>
                    <ListItemDecorator><InfoIcon /></ListItemDecorator>
                    <ListItemContent>
                      <Typography level="body-sm">ETA</Typography>
                      <Typography level="body-xs" color="neutral">
                        {progressData.metrics.estimated_completion || '—'}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                </List>
              </CardContent>
            </Card>

            {/* Deployment Info */}
            <Card variant="outlined">
              <CardContent>
                <Typography level="h4" mb={2}>Deployment Info</Typography>
                <List size="sm">
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body-sm">Environment</Typography>
                      <Typography level="body-xs" color="neutral">
                        {progressData.deployment.environment}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body-sm">Version</Typography>
                      <Typography level="body-xs" color="neutral">
                        v{progressData.deployment.version}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body-sm">Deployed By</Typography>
                      <Typography level="body-xs" color="neutral">
                        {progressData.deployment.deployed_by}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                  <ListItem>
                    <ListItemContent>
                      <Typography level="body-sm">Started</Typography>
                      <Typography level="body-xs" color="neutral">
                        {new Date(progressData.deployment.created_at).toLocaleString()}
                      </Typography>
                    </ListItemContent>
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};